/**
 * GitHubCallbackPage — OAuth callback handler for GitHub
 *
 * Loaded in a popup after the user authorizes on GitHub.
 * Exchanges the authorization code for a session, then
 * posts the result back to the opener via postMessage.
 */

import { useEffect, useRef, useState } from 'react';
import { makeStyles, mergeClasses, tokens, Spinner, Text, Button } from '@coreai/fluentui-react';
import { CheckmarkCircleFilled, DismissCircleFilled } from '@fluentui/react-icons';
import { exchangeGitHubCode } from '@/services/githubAuthService';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground1,
    textAlign: 'center',
  },
  icon: {
    fontSize: '48px',
    marginBottom: tokens.spacingVerticalL,
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalS,
  },
  message: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    maxWidth: '400px',
  },
  errorMessage: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
    maxWidth: '400px',
    wordBreak: 'break-word',
  },
  closeButton: {
    marginTop: tokens.spacingVerticalL,
  },
});

type CallbackStatus = 'processing' | 'success' | 'error';

export const GitHubCallbackPage = () => {
  const styles = useStyles();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [error, setError] = useState<string>();
  const didExchange = useRef(false);
  const { getOttoToken } = useAuth();

  useEffect(() => {
    // Guard against React strict mode double-fire
    if (didExchange.current) return;
    didExchange.current = true;

    async function exchangeCode() {
      document.title = 'Signing in to GitHub...';

      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');

      if (!code) {
        setStatus('error');
        setError('No authorization code received from GitHub.');
        document.title = 'GitHub Sign-in Failed';
        return;
      }

      // CSRF validation: state must match what the opener stored
      let expectedState: string | null = null;
      try {
        expectedState = window.opener?.sessionStorage?.getItem('github_oauth_state') ?? null;
      } catch {
        // Cross-origin or blocked (e.g. Safari strict tracking) — fall back to own sessionStorage
        expectedState = sessionStorage.getItem('github_oauth_state');
      }

      if (!state || state !== expectedState) {
        setStatus('error');
        setError('OAuth state mismatch — possible CSRF attack. Please try again.');
        document.title = 'GitHub Sign-in Failed';
        return;
      }

      const projectName = useAppStore.getState().selectedProject ?? '';
      if (!projectName.trim()) {
        setStatus('error');
        setError('No environment selected. Please select an environment and try again.');
        document.title = 'GitHub Sign-in Failed';
        return;
      }

      const token = await getOttoToken();
      if (!token) {
        setStatus('error');
        setError('Failed to acquire authentication token.');
        document.title = 'GitHub Sign-in Failed';
        return;
      }

      const redirectUri = `${window.location.origin}/github/callback`;
      try {
        const user = await exchangeGitHubCode(projectName, token, code, redirectUri);
        setStatus('success');
        document.title = 'Connected to GitHub';
        if (window.opener) {
          window.opener.postMessage(
            { type: 'github-auth-success', user },
            window.location.origin,
          );
        }
        setTimeout(() => window.close(), 1500);
      } catch (err) {
        setStatus('error');
        setError(err instanceof Error ? err.message : String(err));
        document.title = 'GitHub Sign-in Failed';
      }
    }

    exchangeCode();
  }, [getOttoToken]);

  const handleClose = () => window.close();

  return (
    <div className={styles.container}>
      {status === 'processing' && (
        <>
          <Spinner size="large" />
          <Text className={styles.title}>Signing in...</Text>
          <Text className={styles.message}>
            Connecting to GitHub, please wait.
          </Text>
        </>
      )}

      {status === 'success' && (
        <>
          <CheckmarkCircleFilled className={mergeClasses(styles.icon, styles.successIcon)} />
          <Text className={styles.title}>Connected to GitHub</Text>
          <Text className={styles.message}>
            This window will close automatically.
          </Text>
        </>
      )}

      {status === 'error' && (
        <>
          <DismissCircleFilled className={mergeClasses(styles.icon, styles.errorIcon)} />
          <Text className={styles.title}>GitHub Sign-in Failed</Text>
          {error && <Text className={styles.errorMessage}>{error}</Text>}
          <Button
            appearance="subtle"
            className={styles.closeButton}
            onClick={handleClose}
          >
            Close this window
          </Button>
        </>
      )}
    </div>
  );
};

GitHubCallbackPage.displayName = 'GitHubCallbackPage';
